/**
 * SchoolPaymentAccount Entity Schemas
 *
 * The Stripe Connect payee that receives funds for a school's charges. A
 * school has many payees one-to-many; the MVP creates exactly one primary
 * payee per school and hides the multi-payee picker. Departmental payees
 * (upper/lower school, theater arts) are a zero-migration additive feature
 * later — every chargeable thing already carries a "which payee receives
 * the money" pointer via `Payment.paymentAccountId`. Underlying Prisma
 * model: `SchoolPaymentAccount` (apps/backend).
 *
 * `onboardingStatus` mirrors Stripe's account readiness signal, driven off
 * `account.updated` -> `charges_enabled` (never `details_submitted` alone),
 * reconciled on-read so a missed webhook can't permanently block a
 * completed onboarding.
 *
 * `lossesPayer` records who backstops an uncollectable negative balance on
 * the connected account — an explicit business decision confirmed before
 * account creation and immutable after (maps 1:1 to Stripe's
 * `controller[losses][payments]` account-creation property).
 */

import { z } from 'zod'

export const PaymentAccountOnboardingStatusSchema = z.enum(['pending', 'onboarding', 'active', 'restricted'])
export type PaymentAccountOnboardingStatus = z.infer<typeof PaymentAccountOnboardingStatusSchema>

export const PaymentLossesPayerSchema = z.enum(['stripe', 'application'])
export type PaymentLossesPayer = z.infer<typeof PaymentLossesPayerSchema>

export const SchoolPaymentAccountSchema = z.object({
  /** SchoolPaymentAccount publicId. */
  id: z.string(),
  stripeConnectAccountId: z.string(),
  onboardingStatus: PaymentAccountOnboardingStatusSchema,
  chargesEnabledAt: z.string().nullable(),
  lossesPayer: PaymentLossesPayerSchema,
  isPrimary: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
})
export type SchoolPaymentAccount = z.infer<typeof SchoolPaymentAccountSchema>
